import { Images, Paperclip, Upload, X } from "lucide-react"
import { useRef, useState } from "react"
import { toast } from "sonner"
import { LibraryPickerDialog } from "@/components/library/library-picker-dialog"
import { PlayBadge } from "@/components/media/asset-media-overlay"
import { PEEK_COMPACT_MENU_MIN_WIDTH, PeekAnchoredMenu, PeekMenuItem } from "@/components/platform/company-peek-parts"
import { ComposerIconButton } from "@/components/ui/composer-icon-button"
import { useTranslation } from "@/hooks/use-translation"
import { librarySourceKey } from "@/lib/library-source"
import type { ShotFrameSource } from "@/lib/workspace-api"

export const AGENT_IMAGE_LIMIT = 10
export const AGENT_VIDEO_LIMIT = 2
export const AGENT_IMAGE_MAX_BYTES = 20 * 1024 * 1024
export const AGENT_VIDEO_MAX_BYTES = 100 * 1024 * 1024

const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"])
const VIDEO_TYPES = new Set(["video/mp4", "video/webm", "video/quicktime"])
const ACCEPT = [...IMAGE_TYPES, ...VIDEO_TYPES].join(",")

export type PendingAgentMediaType = "image" | "video"

export type PendingAgentImage =
  | { key: string; kind: "upload"; file: File; previewUrl: string; mediaType: PendingAgentMediaType }
  | {
      key: string
      kind: "library"
      source: ShotFrameSource
      label: string
      previewUrl: string | null
      mediaType: PendingAgentMediaType
    }

export type AgentImageFileMessages = {
  limit: string
  videoLimit: string
  badType: string
  tooLarge: string
  videoTooLarge: string
}

export function pendingAgentMediaType(file: File): PendingAgentMediaType | null {
  const type = file.type === "image/jpg" ? "image/jpeg" : file.type
  if (IMAGE_TYPES.has(type)) return "image"
  if (VIDEO_TYPES.has(type)) return "video"
  const name = file.name.toLowerCase()
  if (/\.(mp4|webm|mov)$/.test(name)) return "video"
  if (/\.(jpe?g|png|webp|gif)$/.test(name)) return "image"
  return null
}

function videoCount(pending: readonly PendingAgentImage[]): number {
  return pending.filter((item) => item.mediaType === "video").length
}

export function pendingAgentQueueLabel(
  images: readonly PendingAgentImage[],
  labels: { image: string; video: string; mixed: string },
): string {
  const videos = videoCount(images)
  if (videos === 0) return labels.image
  if (videos === images.length) return labels.video
  return labels.mixed
}

/** Дописывает файлы к уже выбранным. Тот же массив, если ни один не подошёл. */
export function appendPendingAgentImages(
  pending: PendingAgentImage[],
  files: readonly File[],
  notify: (message: string) => void,
  messages: AgentImageFileMessages,
): PendingAgentImage[] {
  const next = [...pending]
  let added = false
  for (const file of files) {
    if (next.length >= AGENT_IMAGE_LIMIT) {
      notify(messages.limit)
      break
    }
    const mediaType = pendingAgentMediaType(file)
    if (mediaType == null) {
      notify(messages.badType)
      continue
    }
    if (mediaType === "video" && videoCount(next) >= AGENT_VIDEO_LIMIT) {
      notify(messages.videoLimit)
      continue
    }
    const maxBytes = mediaType === "video" ? AGENT_VIDEO_MAX_BYTES : AGENT_IMAGE_MAX_BYTES
    if (file.size > maxBytes) {
      notify(mediaType === "video" ? messages.videoTooLarge : messages.tooLarge)
      continue
    }
    next.push({
      key: `upload-${file.name}-${file.size}-${file.lastModified}-${next.length}`,
      kind: "upload",
      file,
      previewUrl: URL.createObjectURL(file),
      mediaType,
    })
    added = true
  }
  return added ? next : pending
}

export function releasePendingAgentImage(image: PendingAgentImage) {
  if (image.kind === "upload" && image.previewUrl.startsWith("blob:")) URL.revokeObjectURL(image.previewUrl)
}

export function pendingAgentImagesPayload(images: readonly PendingAgentImage[]): {
  files: File[]
  sources: ShotFrameSource[]
  imageOrder: Array<"upload" | "library">
} {
  return {
    files: images.flatMap((image) => (image.kind === "upload" ? [image.file] : [])),
    sources: images.flatMap((image) => (image.kind === "library" ? [image.source] : [])),
    imageOrder: images.map((image) => (image.kind === "upload" ? "upload" : "library")),
  }
}

export function AgentImageAttach({
  pending,
  onChange,
  disabled,
  projectId,
  frameFormat,
}: {
  pending: PendingAgentImage[]
  onChange: (next: PendingAgentImage[]) => void
  disabled: boolean
  projectId: number | null
  frameFormat: string
}) {
  const { t } = useTranslation("workspace")
  const inputRef = useRef<HTMLInputElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const library = projectId != null && projectId > 0

  const addFiles = (files: File[]) => {
    onChange(
      appendPendingAgentImages(pending, files, (message) => toast.error(message), {
        limit: t("agent.imageLimit"),
        videoLimit: t("agent.videoLimit"),
        badType: t("agent.imageBadType"),
        tooLarge: t("agent.imageTooLarge"),
        videoTooLarge: t("agent.videoTooLarge"),
      }),
    )
  }

  return (
    <>
      {library ? (
        <PeekAnchoredMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          menuMinWidth={PEEK_COMPACT_MENU_MIN_WIDTH}
          trigger={(ref) => (
            <ComposerIconButton
              ref={ref}
              aria-label={t("agent.attachImage")}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              disabled={disabled || pending.length >= AGENT_IMAGE_LIMIT}
              onClick={() => setMenuOpen((prev) => !prev)}
              className={menuOpen ? "bg-ui-surface-hover text-ui-text-primary" : undefined}
            >
              <Paperclip className="h-4 w-4" />
            </ComposerIconButton>
          )}
        >
          <PeekMenuItem
            icon={<Upload />}
            label={t("agent.attachFromComputer")}
            onSelect={() => {
              setMenuOpen(false)
              inputRef.current?.click()
            }}
          />
          <PeekMenuItem
            icon={<Images />}
            label={t("agent.attachFromLibrary")}
            onSelect={() => {
              setMenuOpen(false)
              setLibraryOpen(true)
            }}
          />
        </PeekAnchoredMenu>
      ) : (
        <ComposerIconButton
          aria-label={t("agent.attachImage")}
          disabled={disabled || pending.length >= AGENT_IMAGE_LIMIT}
          onClick={() => inputRef.current?.click()}
        >
          <Paperclip className="h-4 w-4" />
        </ComposerIconButton>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          event.target.value = ""
          if (files.length > 0) addFiles(files)
        }}
      />
      {library && projectId != null ? (
        <LibraryPickerDialog
          open={libraryOpen}
          onOpenChange={setLibraryOpen}
          projectId={projectId}
          frameFormat={frameFormat}
          multiple
          selectionLimit={AGENT_IMAGE_LIMIT - pending.length}
          pickedKeys={pending.flatMap((image) => (image.kind === "library" ? [librarySourceKey(image.source)] : []))}
          subtitle={t("agent.attachFromLibrary")}
          targets={[{ id: "agent", label: t("agent.attach"), accepts: ["image", "video", "previz"] }]}
          onPick={() => undefined}
          onPickMany={(_targetId, picked) => {
            const next = [...pending]
            let videoLimited = false
            for (const item of picked) {
              if (next.length >= AGENT_IMAGE_LIMIT) break
              const mediaType = item.media === "previz" || item.media === "video" ? "video" : item.media
              if (mediaType !== "image" && mediaType !== "video") continue
              if (mediaType === "video" && videoCount(next) >= AGENT_VIDEO_LIMIT) {
                videoLimited = true
                continue
              }
              if (next.some((image) => image.kind === "library" && librarySourceKey(image.source) === item.key))
                continue
              next.push({
                key: item.key,
                kind: "library",
                source: item.source,
                label: item.label,
                previewUrl:
                  mediaType === "video" ? item.thumbUrl || null : item.thumbUrl || item.downloadUrl || item.url,
                mediaType,
              })
            }
            if (videoLimited) toast.error(t("agent.videoLimit"))
            onChange(next)
            setLibraryOpen(false)
          }}
        />
      ) : null}
    </>
  )
}

export function AgentImageIndexBadge({ index }: { index: number }) {
  return (
    <span className="pointer-events-none absolute bottom-0.5 left-0.5 flex h-4 min-w-4 items-center justify-center rounded-sm bg-ui-surface/90 px-1 text-[10px] leading-none text-ui-text-primary tabular-nums">
      {index}
    </span>
  )
}

export function AgentPendingImageChips({
  pending,
  onRemove,
}: {
  pending: PendingAgentImage[]
  onRemove: (key: string) => void
}) {
  const { t } = useTranslation("workspace")
  if (pending.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1 px-2.5 pt-2">
      {pending.map((image, index) => (
        <div key={image.key} className="relative h-12 w-12 overflow-hidden rounded-lg bg-ui-surface">
          {image.mediaType === "video" && image.kind === "upload" && image.previewUrl ? (
            <video src={image.previewUrl} muted playsInline preload="metadata" className="h-full w-full object-cover" />
          ) : image.previewUrl ? (
            <img src={image.previewUrl} alt="" className="h-full w-full object-cover" />
          ) : null}
          {image.mediaType === "video" ? (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <PlayBadge className="h-6 w-6 [&_svg]:h-3 [&_svg]:w-3" />
            </span>
          ) : null}
          <AgentImageIndexBadge index={index + 1} />
          <button
            type="button"
            className="absolute top-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-ui-surface/90 text-ui-text-muted"
            aria-label={t("agent.imageRemove")}
            onClick={() => onRemove(image.key)}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  )
}
